import JSZip from "jszip"
import * as XLSX from "xlsx-js-style"

const findExcelInZip = async zipContent => {
  const fileEntries = Object.entries(zipContent.files)
  const excelFiles = fileEntries.filter(
    ([filename, fileData]) =>
      !fileData.dir && (filename.endsWith(".xlsx") || filename.endsWith(".xls"))
  )

  const processExcelFile = async ([filename, fileData]) => {
    try {
      const arrayBuffer = await fileData.async("arraybuffer")
      const workbook = XLSX.read(new Uint8Array(arrayBuffer), {
        type: "array",
      })

      const [firstSheetName] = workbook.SheetNames
      const worksheetName = workbook.Sheets.Rostros ? "Rostros" : firstSheetName
      const worksheet = workbook.Sheets[worksheetName]

      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
      })

      if (jsonData.length >= 4) {
        return { filename, jsonData }
      }
      return null
    } catch (error) {
      return null
    }
  }

  // Procesar todos los archivos y tomar el primero válido
  const results = await Promise.all(excelFiles.map(processExcelFile))
  const validResult = results.find(result => result !== null)

  return validResult || null
}

const processExcelData = (excelData, imageMap) => {
  const headers = excelData[3]
  const requiredHeaders = ["nombres", "identificacion"]
  const hasRequiredHeaders = requiredHeaders.every(reqHeader =>
    headers.some(
      header =>
        header &&
        header.toString().toLowerCase().trim().includes(reqHeader.toLowerCase())
    )
  )

  if (!hasRequiredHeaders) {
    throw new Error(
      'El archivo Excel no tiene el formato correcto. Debe incluir columnas: "Nombres", "Identificacion"'
    )
  }

  const columnIndexes = {}
  headers.forEach((header, index) => {
    if (header) {
      const normalizedHeader = header.toString().toLowerCase().trim()
      if (normalizedHeader === "pin") columnIndexes.pin = index
      if (normalizedHeader.includes("tag")) columnIndexes.tags = index
      if (normalizedHeader.includes("qr")) columnIndexes.qrs = index
      if (
        normalizedHeader.includes("nombre") &&
        !normalizedHeader.includes("imagen")
      )
        columnIndexes.nombres = index
      if (normalizedHeader.includes("identificaci"))
        columnIndexes.identificacion = index
      if (
        normalizedHeader.includes("unidad") ||
        normalizedHeader.includes("hab")
      )
        columnIndexes.unidad = index
      if (normalizedHeader.includes("pisos")) columnIndexes.pisos = index
      if (normalizedHeader.includes("tipo")) columnIndexes.tipo = index
      if (normalizedHeader.includes("estado")) columnIndexes.estado = index
      if (normalizedHeader.includes("observ"))
        columnIndexes.observaciones = index
      if (normalizedHeader.includes("imagen")) columnIndexes.imagen = index
    }
  })

  const isEmptyRow = row =>
    !row || row.every(cell => !cell || cell.toString().trim() === "")

  const processedData = []
  const errors = []

  for (let i = 4; i < excelData.length; i += 1) {
    const row = excelData[i]

    if (!isEmptyRow(row)) {
      const rowData = {}
      const rowErrors = []

      rowData.nombre = row[columnIndexes.nombres]
        ? row[columnIndexes.nombres].toString().trim()
        : ""
      rowData.identificacion = row[columnIndexes.identificacion]
        ? row[columnIndexes.identificacion].toString().trim()
        : ""
      rowData.destino = row[columnIndexes.unidad]
        ? row[columnIndexes.unidad].toString().trim()
        : ""
      rowData.pisos = row[columnIndexes.pisos]
        ? (() => {
            const pisosString = row[columnIndexes.pisos].toString().trim()
            if (!pisosString) return []
            return pisosString
              .split(",")
              .map(p => {
                const subsuelo = p.match(/^S(\d+)$/i)
                if (subsuelo) return -parseInt(subsuelo[1], 10)
                const num = parseInt(p, 10)
                return Number.isNaN(num) ? null : num
              })
              .filter(p => p !== null)
          })()
        : []
      rowData.tipo = row[columnIndexes.tipo]
        ? row[columnIndexes.tipo].toString().trim()
        : "propietario"
      rowData.estado = row[columnIndexes.estado]
        ? row[columnIndexes.estado].toString().trim()
        : "activo"
      rowData.observaciones = row[columnIndexes.observaciones]
        ? row[columnIndexes.observaciones].toString().trim()
        : ""
      rowData.imagen = row[columnIndexes.imagen]
        ? row[columnIndexes.imagen].toString().trim()
        : ""

      rowData.pin = row[columnIndexes.pin]
        ? row[columnIndexes.pin].toString().replace(/\D/g, "").substring(0, 8)
        : ""

      if (rowData.pin && (rowData.pin.length < 4 || rowData.pin.length > 8)) {
        rowErrors.push("PIN debe tener entre 4 y 8 dígitos")
      }

      const parseCodes = (raw, tipo) => {
        if (!raw) return []
        return raw
          .toString()
          .split(",")
          .map(s => s.replace(/[^\x20-\x7E]/g, "").trim())
          .filter(s => s.length > 0)
          .map(codigo => ({ tipo, codigo, label: "" }))
      }

      rowData.tarjeta = [
        ...parseCodes(row[columnIndexes.tags], "tag"),
        ...parseCodes(row[columnIndexes.qrs], "qr"),
      ]

      if (rowData.imagen) {
        const nombreSinExtension = rowData.imagen
          .toLowerCase()
          .replace(/\.[^/.]+$/, "")
        const imagenEncontrada =
          imageMap.get(nombreSinExtension) ||
          imageMap.get(rowData.imagen.toLowerCase())

        if (!imagenEncontrada) {
          rowErrors.push(
            `Imagen '${rowData.imagen}' no encontrada en el archivo ZIP`
          )
        }
      }

      // Validaciones
      if (!rowData.nombre) {
        rowErrors.push("Nombres es requerido")
      }

      if (!rowData.identificacion) {
        rowErrors.push("Identificacion es requerida")
      } else if (!/^\d+$/.test(rowData.identificacion)) {
        rowErrors.push(
          "Identificacion debe contener solo números (sin puntos, guiones o espacios)"
        )
      }

      // Validar tipo
      const validTypes = [
        "propietario",
        "familiar",
        "empleado",
        "visitante",
        "arrendatario",
        "inquilino",
      ]
      if (rowData.tipo && !validTypes.includes(rowData.tipo.toLowerCase())) {
        rowErrors.push(
          `Tipo '${rowData.tipo}' no es válido. Use: ${validTypes.join(", ")}`
        )
      } else if (rowData.tipo) {
        rowData.tipo = rowData.tipo.toLowerCase()
      }

      // Validar estado
      const validStates = ["activo", "bloqueado"]
      if (
        rowData.estado &&
        !validStates.includes(rowData.estado.toLowerCase())
      ) {
        rowErrors.push(
          `Estado '${rowData.estado}' no es válido. Use: ${validStates.join(", ")}`
        )
      } else if (rowData.estado) {
        rowData.estado = rowData.estado.toLowerCase()
      }

      if (rowErrors.length > 0) {
        errors.push({
          row: i + 1,
          errors: rowErrors,
          data: rowData,
        })
      } else {
        processedData.push(rowData)
      }
    }
  }

  return { processedData, errors }
}

export const processCompleteZip = async zipFile =>
  new Promise((resolve, reject) => {
    const processZip = async () => {
      try {
        const zip = new JSZip()
        const zipContent = await zip.loadAsync(zipFile)

        // 1. Buscar y procesar el archivo Excel
        const excelResult = await findExcelInZip(zipContent)

        if (!excelResult) {
          reject(new Error("No se encontró un archivo Excel válido en el ZIP"))
          return
        }

        // 2. CONSTRUIR imageMap
        const imageMap = new Map()
        const imagePromises = []

        Object.entries(zipContent.files).forEach(([filename, fileData]) => {
          if (!fileData.dir && /\.(jpg|jpeg|png)$/i.test(filename)) {
            const imageName = filename.substring(filename.lastIndexOf("/") + 1)
            const nameWithoutExt = imageName
              .toLowerCase()
              .replace(/\.[^/.]+$/, "")

            imagePromises.push(
              fileData.async("blob").then(blob => {
                const imageFile = new File([blob], imageName, {
                  type: blob.type,
                })
                imageMap.set(nameWithoutExt, imageFile)
                imageMap.set(imageName.toLowerCase(), imageFile)
              })
            )
          }
        })

        await Promise.all(imagePromises)

        // 3. Procesar datos del Excel
        const { processedData, errors } = processExcelData(
          excelResult.jsonData,
          imageMap
        )

        resolve({
          success: true,
          data: processedData,
          errors,
          totalRows: processedData.length + errors.length,
          validRows: processedData.length,
          invalidRows: errors.length,
          availableImages: Array.from(imageMap),
          excelFile: excelResult.filename,
        })
      } catch (error) {
        reject(new Error(`Error al procesar el ZIP: ${error.message}`))
      }
    }

    processZip()
  })
